import * as React from 'react';
import { keyBy } from 'lodash';

import Slider from 'react-slick';
import Spinner from 'components/spinner';
import Lightbox from 'components/lightbox';
import ProgressBlockTier from './progress-block-tier';
import ProgressBlockHeader from './progress-block-header';
import ProgressBlockContainer from './progress-block-container';
import ProgressBlockExpandButton from './progress-block-expand-button';
import { ProgressBlockBar } from './progress-block-bar';
import { Icon, ICON_NAME } from 'components/icon';
import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';

import { IProgressBlockProps } from './progress-block.types';
import { IRewardTierItem } from '@sitegen/common/types/rewards-tiers';

import { REWARDS_TIER_TEXT_KEY_PREFIX } from '@sitegen/common/constants/website-content';
import TEXT_KEYS, {
    REWARD_TIERS_MOBILE_NUMBER,
    OPEN_ALL_REWARDS_ANALYTICS,
    OPEN_REWARD_ITEM_ANALYTICS
} from './progress-block.constants';

import rewardPlaceholder from '../../assets/reward-placeholder.svg';
import './progress-block.styles.less';
import { getSteps, getMobileSteps, canSlidePrev, canSlideNext } from './progress-block.utils';
import { getModeClass } from 'utils/css';
import { WEBSITE_MODE } from '@sitegen/common/constants';

class ProgressBlock extends React.Component<IProgressBlockProps, IProgressBlockState> {
    public state: IProgressBlockState = {
        currentSlide: 0,
        expandButtonOpened: false,
        lightboxOpened: false
    };

    private getAllProgressTiers = (): JSX.Element[] => {
        const {
            rewardTiers,
            rewardTiersAssets,
            dangerouslyTranslateWithHTML: translateKey,
            shouldInheritStyles
        } = this.props;
        const assetsMap = keyBy(rewardTiersAssets, asset => asset.tierId);

        return Object.keys(rewardTiers)
            .map((levelNumber, index) => {
                const asset = assetsMap[levelNumber] as IRewardTierItem | undefined;
                return <ProgressBlockTier
                    key={levelNumber}
                    shouldInheritStyles={shouldInheritStyles}
                    imageSrc={asset && asset.imageSrc}
                    title={translateKey(`${REWARDS_TIER_TEXT_KEY_PREFIX}${levelNumber}`)}
                    onClick={() => {
                        this.openLightboxWithImage(asset?.imageSrc);
                        const rewardItemId = ((asset && asset.tierId) || ++index).toString();
                        AnalyticsService.sendFlowEvent(OPEN_REWARD_ITEM_ANALYTICS(rewardItemId));
                    }}
                />;
            });
    };

    private openLightboxWithImage = (imageSrc?: string) => {
        this.setState({
            currentImage: imageSrc || rewardPlaceholder,
            lightboxOpened: true
        });
    };

    private setActualSlideIndex = (_: Object, index: number) => {
        this.setState({
            currentSlide: index
        });
    };

    private closeLightbox = () => {
        this.setState({
            lightboxOpened: false
        });
    };

    private getTiersSlider = (): JSX.Element => {
        const { rewardTiers, dangerouslyTranslateWithHTML: translateKey } = this.props;
        const { currentSlide } = this.state;
        const sliderSettings = {
            slidesToShow: getSteps(rewardTiers, currentSlide, translateKey).length,
            infinite: false,
            beforeChange: this.setActualSlideIndex,
            nextArrow: this.getArrowButton(),
            prevArrow: this.getArrowButton(true)
        };
        return (
            <Slider {...sliderSettings}>
                {this.getAllProgressTiers()}
            </Slider>
        );
    };

    private getArrowButton = (reverseDirection = false): JSX.Element => {
        const iconName = reverseDirection ? ICON_NAME.ANGLE_LEFT : ICON_NAME.ANGLE_RIGHT;
        return (
            <div>
                <Icon name={iconName}/>
            </div>
        );
    };

    private onExpandButtonClick = () => {
        this.setState(prevState => ({
            expandButtonOpened: !prevState.expandButtonOpened
        }));
    };

    private getMobileProgressTiers = (): JSX.Element[] => {
        return this.state.expandButtonOpened
            ? this.getAllProgressTiers()
            : this.getAllProgressTiers()
                .filter((_, index) => index < REWARD_TIERS_MOBILE_NUMBER);
    };

    private openRewardsLightbox = () => {
        const { mode, showRewardsImageLightbox, rewardsImageSrc } = this.props;
        showRewardsImageLightbox(true);
        // we call 'openLightboxWithImage' with rewardsImageSrc cause in embedded mode we haven't static part (which
        // has lightbox for all rewards) thats why we call lightbox here
        // for future need to take out lightbox to content component
        // tech dept: https://gitlab-ncsa.ubisoft.org/Sitegen/sitegen-main/-/issues/177
        if (mode === WEBSITE_MODE.EMBEDDED) {
            this.openLightboxWithImage(rewardsImageSrc);
        }

        AnalyticsService.sendFlowEvent(OPEN_ALL_REWARDS_ANALYTICS);
    };

    private getLightbox = (): React.ReactNode => {
        return this.state.lightboxOpened && this.state.currentImage && (
            <Lightbox
                onCloseRequest={this.closeLightbox}
                mainSrc={this.state.currentImage}
            />
        );
    };

    private getExpandButton = (): React.ReactNode => {
        const { expandButtonOpened } = this.state;
        const { dangerouslyTranslateWithHTML, shouldInheritStyles } = this.props;
        const basePageStyleClass = getModeClass('sitegen-friend-referral-progress-block', shouldInheritStyles);

        return REWARD_TIERS_MOBILE_NUMBER < this.getAllProgressTiers().length
            ? <div className={`${basePageStyleClass}-expand`}>
                <ProgressBlockExpandButton
                    shouldInheritStyles={shouldInheritStyles}
                    onClick={() => this.onExpandButtonClick()}
                    iconName={expandButtonOpened ? ICON_NAME.ANGLE_TOP : ICON_NAME.ANGLE_DOWN}
                    text={
                        expandButtonOpened ?
                            dangerouslyTranslateWithHTML(TEXT_KEYS.CLOSE_SEE_MORE) :
                            dangerouslyTranslateWithHTML(TEXT_KEYS.SEE_MORE)
                    }
                />
            </div>
            : null;
    };

    private getProgressBlock = () => {
        const { currentSlide, expandButtonOpened } = this.state;
        const {
            dangerouslyTranslateWithHTML: translateKey,
            convertedFriendsCount,
            rewardTiers,
            shouldInheritStyles
        } = this.props;

        const basePageStyleClass = getModeClass('sitegen-friend-referral-progress-block', shouldInheritStyles);

        return (
            <div className={basePageStyleClass}>
                {this.getLightbox()}

                <ProgressBlockHeader
                    shouldInheritStyles={shouldInheritStyles}
                    title={translateKey(TEXT_KEYS.TITLE)}
                    subtitle={translateKey(TEXT_KEYS.SUBTITLE)}
                    link={translateKey(TEXT_KEYS.SEE_FULL)}
                    onClick={this.openRewardsLightbox}
                />

                <ProgressBlockContainer shouldInheritStyles={shouldInheritStyles}>
                    {this.getTiersSlider()}
                </ProgressBlockContainer>

                <div className={`${basePageStyleClass}-bar`}>
                    <ProgressBlockBar
                        shouldInheritStyles={shouldInheritStyles}
                        value={convertedFriendsCount}
                        steps={getSteps(rewardTiers, currentSlide, translateKey)}
                        leftTail={canSlidePrev(currentSlide)}
                        rightTail={canSlideNext(rewardTiers, currentSlide)}
                    />
                </div>

                <div className={`${basePageStyleClass}-mobile`}>
                    <div className={`${basePageStyleClass}-mobile__inner`}>
                        <div className={`${basePageStyleClass}-mobile__bar`}>
                            <ProgressBlockBar
                                shouldInheritStyles={shouldInheritStyles}
                                value={convertedFriendsCount}
                                steps={getMobileSteps(rewardTiers, expandButtonOpened, translateKey)}
                                vertical
                            />
                        </div>

                        <div className={`${basePageStyleClass}-mobile__container`}>
                            {this.getMobileProgressTiers()}
                        </div>
                    </div>

                    {this.getExpandButton()}
                </div>
            </div>
        );
    };

    public render() {
        return this.props.loading ? <Spinner/> : this.getProgressBlock();
    }
}

interface IProgressBlockState {
    readonly currentSlide: number;
    readonly expandButtonOpened: boolean;
    readonly currentImage?: string;
    readonly lightboxOpened: boolean;
}

export default ProgressBlock;
export {
    Slider,
    Spinner,
    ProgressBlockBar,
    ProgressBlockHeader,
    ProgressBlockContainer,
    ProgressBlockExpandButton,
    IProgressBlockProps
};
